import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as common from '../common'
import * as schema from '../schema'

import type { Context, Entity, TypeParams } from './types'

const CHUNK_QUANTITY = 1000

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(schema.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      if (!context?.modelId) {
        // If we do not have a current entity, we can't have any records
        return rxjs.of([])
      }

      const query = {
        // TODO: add filter
        noUnpacking: true,
        quantity: request?.pagination?.quantity,
        selection: {
          '{}': {
            values: {
              // Use `@.*` (GetRecord) to select ALL attributes at once.
              // Reference values come back in entity format
              // (`{ entityId, recordId }`); ancestor names are resolved
              // separately via the reference-records state.
              '@.*': {},
            },
          },
        },
        skip: request?.pagination?.skip,
        // Add sorting to the query. Since `sort` can be undefined, check if
        // it exists before passing the sorting parameter.
        sort: request?.sort
          ? {
              // Sorting must be done by PartialId, but since the type of
              // `by` is a string, we need to assert that `by` is PartialId.
              // Note: We use a type assertion here with a warning instead
              // of strict validation. If `by` isn't PartialId, we don't
              // want to reject the request outright since the backend might
              // still handle it and return data. It's preferable to let the
              // backend throw an error if necessary.
              by: {
                '@.': TypeAsserter.validateButJustWarn(
                  request.sort.by,
                  PartialId.assertIsPartialId,
                ),
              } satisfies ents.core.dms.data.expression.fn.Fn,
              descending: request.sort.descending,
            }
          : undefined,
        target: context.modelId,
        ...(request.filter?.searchTerm
          ? {
              filter: {
                isSimilar: [
                  {
                    '@.': '!search_string',
                  },
                  request.filter?.searchTerm,
                ],
              },
            }
          : {}),
      }

      return common.gql.queries.data.execute({
        ...context,
        // Stringify the query because GraphQL expects an AWSJSON type.
        // Since we don't have a TypeScript representation for AWSJSON, we
        // use 'any' type.
        query: JSON.stringify(query),
      }) satisfies rxjs.Observable<unknown> as rxjs.Observable<
        readonly Entity[]
      >
    },
  )

  /** @inheritdoc  */
  public count: ec.models.Api<TypeParams>['count'] = (context, request) => {
    if (!context?.modelId) {
      // If we do not have a current entity, we can't have any records
      return rxjs.of(0)
    }
    const query = {
      selection: {
        '#': {
          '@': 'records',
        },
      },
      subqueries: {
        records: {
          selection: {
            '@.': '!id',
          },
          target: context.modelId,
          ...(request?.filter?.searchTerm
            ? {
                filter: {
                  isSimilar: [
                    {
                      '@.': '!search_string',
                    },
                    request?.filter?.searchTerm,
                  ],
                },
              }
            : {}),
        },
      },
    }
    return common.gql.queries.data.execute({
      ...context,
      // Stringify the query because GraphQL expects an AWSJSON type. Since we
      // don't have a TypeScript representation for AWSJSON, we use 'any' type.
      query: JSON.stringify(query),
    }) satisfies rxjs.Observable<unknown> as rxjs.Observable<number>
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    context => {
      if (!context?.modelId) {
        // If we do not have a current entity, we can't get attributes
        return rxjs.of([])
      }
      const { modelId } = context
      return this._schemaFeature.selectors.schema.asObservable.pipe(
        rxjs.map(_schema =>
          // NOTE: Creates a new list to avoid accidentally modifying the object
          // referenced in the state.
          _schema
            .getAttributesOf(modelId)
            .map<ec.models.Attribute<TypeParams>>(attribute => {
              const id: ents.core.dms.data.Record.Values.Key =
                ents.core.dms.models.Id.Parts.from(attribute.id).partialId
              const commonAttribute =
                ents.core.dms.models.Attribute.toCommonAttribute(
                  attribute,
                  _schema,
                )
              return {
                ...commonAttribute,
                getValueFrom: entity => entity.values[id],
                id,
              }
            }),
        ),
      )
    }

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    if (!context?.modelId) {
      // If we do not have a current entity, we can't create records
      return rxjs.throwError(
        // TODO: better error handling
        () => new Error('Unable to determine current entity'),
      )
    }
    TypeAsserter.assertIsNotNullish(context)
    const { tenantId, zoneId, modelId } = context
    return common.gql.mutations.putZoneData.executor({
      insertion: {
        inputs: {
          records: [
            JSON.stringify({
              ...input,
              [ents.core.dms.data.Record.Id.KEY]: undefined,
            }),
          ],
        },
        modelId,
      },
      tenantId,
      zoneId,
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    const { tenantId, zoneId, modelId } = context
    if (!modelId) {
      // If we do not have a current entity, we can't delete records
      return rxjs.throwError(
        // TODO: better error handling
        () => new Error('Unable to determine current entity'),
      )
    }

    return common.gql.mutations.deleteZoneData.executor({
      deletion: { ids: [id], modelId },
      tenantId,
      zoneId,
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    TypeAsserter.assertIsNotNullish(context.modelId)
    const { tenantId, zoneId, modelId } = context
    return common.gql.mutations.putZoneData.executor({
      insertion: {
        inputs: {
          records: [JSON.stringify(updates)],
        },
        modelId,
      },
      tenantId,
      zoneId,
    })
  }
}
